import { useState } from 'react';
import './bracket-view.styles.scss';
import { Link } from 'react-router-dom';
import TeamBadge from '@/components/SHARED/team-badge/team-badge.component';
import TeamName from '@/components/SHARED/team-name/team-name.component';

// One bracket, a round at a time (works on a phone). bracket: from
// summary/postseason. rounds: which round indexes to offer (default all).
// filter(matchup): optional, e.g. one region of the national tournament.
const BracketView = ({ bracket, teams, teamLink, gameLink, highlightId, rounds, filter }) => {

    const roundList = rounds ?? bracket.rounds.map((_, i) => i);
    // Open on the round being played (or the last one when it's over).
    const current = roundList.find(r => bracket.rounds[r].matchups.some(m => !m.winner)) ?? roundList.at(-1);
    const [ round, setRound ] = useState(current);
    const shown = roundList.includes(round) ? round : current;
    const matchups = bracket.rounds[shown].matchups.filter(m => !filter || filter(m)).filter(m => !m.bye);

    return (
        <div className='bracket-view'>
            {roundList.length > 1 &&
                <div className='round-chips' role='tablist'>
                    {roundList.map(r => (
                        <button
                            key={r}
                            role='tab'
                            aria-selected={r === shown}
                            className={`chip ${r === shown ? 'active' : ''}`}
                            onClick={() => setRound(r)}>
                            {bracket.rounds[r].name}
                        </button>
                    ))}
                </div>
            }
            {matchups.length === 0 && <p className='meta'>No games in this round.</p>}
            <div className='matchups'>
                {matchups.map(m => (
                    <Matchup key={`${m.round}-${m.index}`} m={m} teams={teams} teamLink={teamLink} gameLink={gameLink} highlightId={highlightId} />
                ))}
            </div>
        </div>
    )
}

const Matchup = ({ m, teams, teamLink, gameLink, highlightId }) => {
    const series = m.bestOf > 1;
    const single = !series && m.games[0];
    const score = (entry) => {
        if (!entry) return '';
        if (series) return m.wins[entry === m.top ? 'top' : 'bottom'];
        if (!single) return '';
        return single.home === entry.team ? single.result.home : single.result.away;
    };
    const side = (entry) => {
        const team = entry && teams?.[entry.team];
        const cls = [
            'matchup-team',
            m.winner && entry?.team === m.winner ? 'won' : '',
            m.winner && entry && entry.team !== m.winner ? 'lost' : '',
            entry?.team && entry.team === highlightId ? 'mine' : '',
        ].join(' ');
        return (
            <div className={cls}>
                <span className='seed'>{entry?.seed ?? ''}</span>
                {team ?
                    <Link to={teamLink(team.id)} className='matchup-name'>
                        <TeamBadge team={team} size='sm' /> <span><TeamName team={team} /></span>
                    </Link> :
                    <span className='matchup-name meta'>To be decided</span>
                }
                <span className='matchup-score'>{score(entry)}</span>
            </div>
        )
    };
    const status = series ?
        (m.winner ? `${teams?.[m.winner]?.name ?? ''} won ${Math.max(m.wins.top, m.wins.bottom)}-${Math.min(m.wins.top, m.wins.bottom)}` :
            m.games.length ? `Best of ${m.bestOf} · ${seriesText(m, teams)}` : `Best of ${m.bestOf}`) :
        (single ? `Final${single.result.ot ? ` (${single.result.ot === 1 ? 'OT' : `${single.result.ot}OT`})` : ''} · Day ${single.day}` :
            m.pending ? 'Up next' : '');

    return (
        <div className='g-card matchup'>
            {side(m.top)}
            {side(m.bottom)}
            <div className='matchup-meta meta'>
                {single ? <Link to={gameLink(single.id)}>{status}</Link> : status}
            </div>
            {series && m.games.length > 0 &&
                <div className='series-games'>
                    {m.games.map((g, i) => (
                        <Link key={g.id} to={gameLink(g.id)} className='series-game'>
                            G{i + 1} {teams?.[g.home]?.abbr} {g.result.home}-{g.result.away} {teams?.[g.away]?.abbr}
                        </Link>
                    ))}
                </div>
            }
        </div>
    )
}

function seriesText(m, teams) {
    if (m.wins.top === m.wins.bottom) return `Tied ${m.wins.top}-${m.wins.bottom}`;
    const leader = m.wins.top > m.wins.bottom ? m.top : m.bottom;
    return `${teams?.[leader.team]?.abbr ?? ''} leads ${Math.max(m.wins.top, m.wins.bottom)}-${Math.min(m.wins.top, m.wins.bottom)}`;
}

export default BracketView
